import { throwError, timer } from 'rxjs';
import { mergeMap, finalize } from 'rxjs/operators';

const DEFAULT_CONSTANT_RETRY = 3000;

export const genericRetryStrategy =
  ({
    maxRetryAttempts = 5,
    scalingDuration = 2000,
    excludedStatusCodes = [404, 422],
    logger = {
      nodeLog: e => {
        console.log(e);
      },
    },
    meta = {},
  } = {}) =>
  attempts =>
    attempts.pipe(
      mergeMap((error, i) => {
        if (error.status === 401) {
          window.location = '/';
          return throwError('log in');
        }
        if (error.name === 'AbortError') {
          logger.nodeLog({ message: 'Request aborted. Exit.' });
          return throwError(error);
        }

        if (!!error.request && error.request.body instanceof FormData) {
          const _chunk = {
            file_path: error.request.body.get('chunk[file_path]'),
            file_size: error.request.body.get('chunk[file_size]'),
            offset: error.request.body.get('chunk[offset]'),
            chunk_size: error.request.body.get('chunk[chunk_size]'),
          };
          logger.nodeLog({ message: `not uploaded, retry - ${JSON.stringify(_chunk)}` });
        }
        const retryAttempt = i + 1;

        // if maximum number of retries have been met
        // or response is a status code we don't wish to retry, throw error
        if (
          (maxRetryAttempts !== -1 && retryAttempt > maxRetryAttempts) ||
          excludedStatusCodes.find(e => e === error.status)
        ) {
          logger.nodeLog({ message: `Last attempt ${retryAttempt} failed! Exit.`, ...meta });
          return throwError(error);
        }
        logger.nodeLog({
          message: `Attempt ${retryAttempt} of ${maxRetryAttempts}: retrying in ${
            retryAttempt * scalingDuration
          }ms, error_message: ${error.message || error.status}`,
          ...meta,
        });
        // retry after 1s, 2s, etc...
        return timer(retryAttempt * scalingDuration);
      }),
      finalize(a => {
        console.log(attempts);
        logger.nodeLog({ message: 'Retry is done!', ...meta, a });
      })
    );

export const uploadRetryStrategy =
  ({
    maxRetryAttempts = 5,
    scalingDuration = 2000,
    logger = {
      nodeLog: e => {
        console.log(e);
      },
    },
    meta = {},
    continueUpload,
    status,
  } = {}) =>
  attempts =>
    attempts.pipe(
      mergeMap((error, i) => {
        if (error.name === 'AbortError') {
          logger.nodeLog({ message: 'Request aborted. Exit.' });
          return throwError(error);
        }

        const retryAttempt = i + 1;

        // if maximum number of retries have been met
        // or response is a status code we don't wish to retry, throw error
        if ((maxRetryAttempts !== -1 && retryAttempt > maxRetryAttempts) || status.paused === true) {
          logger.nodeLog({ message: `Last attempt ${retryAttempt} failed! Wait for continue.`, ...meta });
          status.paused = true;
          return continueUpload;
        }

        logger.nodeLog({
          message: `Attempt ${retryAttempt} of ${maxRetryAttempts}: retrying in ${
            retryAttempt * scalingDuration
          }ms, error_message: ${error.message || error.status}`,
          ...meta,
        });
        // retry after 1s, 2s, etc...
        status.paused = false;
        return timer(retryAttempt * scalingDuration);
      }),
      finalize(a => {
        console.log(attempts);
        logger.nodeLog({ message: 'Retry is done!', ...meta, a });
      })
    );

export const maxRetryAttempts = 15;

export const constantRetryStrategy =
  ({ statusCodes = [] } = {}) =>
  attempts =>
    attempts.pipe(
      mergeMap(error => {
        if (error.status === 401) {
          window.location = '/';
          return throwError('log in');
        }
        const duration = (error.response && +error.response.timeout * 1000) || DEFAULT_CONSTANT_RETRY;

        if (statusCodes.length > 0 && !statusCodes.find(e => e === error.status)) {
          return throwError(error);
        }

        return timer(duration);
      }),
      finalize(() => {})
    );
